import {
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CodeBlock,
  InlineCode,
  Badge,
  Banner,
  TableOfContents,
  Animate,
  useIsDesktop
} from 'wss3-forge'
import {
  Key20Regular,
  Link20Regular,
  Person20Regular,
  ShieldCheckmark20Regular,
  Code20Regular,
  Settings20Regular
} from '@fluentui/react-icons'

export function DocsIntegration() {
  const isDesktop = useIsDesktop()

  const tocItems = [
    { id: 'step1', title: '1. Creer une application' },
    { id: 'step2', title: '2. Configurer le SDK' },
    { id: 'step3', title: '3. Bouton de connexion' },
    { id: 'step4', title: '4. Page callback' },
    { id: 'step5', title: '5. Utiliser l\'auth' },
    { id: 'example', title: 'Exemple complet' }
  ]

  return (
    <div className="page-content">
      <HStack gap="xl" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <VStack gap="xl">
            <Animate type="fadeIn">
              <VStack gap="sm" style={{ alignItems: 'flex-start' }}>
                <Badge variant="primary">Integration</Badge>
                <Heading level={1}>Guide d'integration</Heading>
                <Text color="muted" size="lg">
                  Integrez Webba ID dans votre application React en 5 etapes.
                </Text>
              </VStack>
            </Animate>

            {/* Step 1 */}
            <div id="step1" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={100}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      <Badge variant="primary">Etape 1</Badge>
                      <Key20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Creer une application</Heading>
                    </HStack>

                    <Text>
                      Dans le Dashboard Webba ID, creez une nouvelle application pour obtenir vos identifiants :
                    </Text>

                    <Card variant="outlined" padding="md">
                      <VStack gap="sm">
                        <HStack gap="md" style={{ alignItems: 'center' }}>
                          <InlineCode>API Key</InlineCode>
                          <Text size="sm" color="muted">Cle Supabase anon de Webba ID</Text>
                        </HStack>
                        <HStack gap="md" style={{ alignItems: 'center' }}>
                          <InlineCode>Client ID</InlineCode>
                          <Text size="sm" color="muted">Identifiant public (ex: <InlineCode>wba_abc123...</InlineCode>)</Text>
                        </HStack>
                        <HStack gap="md" style={{ alignItems: 'center' }}>
                          <InlineCode>Client Secret</InlineCode>
                          <Text size="sm" color="muted">Cle secrete (ex: <InlineCode>wbs_xyz789...</InlineCode>)</Text>
                        </HStack>
                      </VStack>
                    </Card>

                    <Banner variant="info" title="Developer Portal">
                      Creez votre application sur https://account.webba-creative.com/platform/applications
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            {/* Step 2 */}
            <div id="step2" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={150}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      <Badge variant="primary">Etape 2</Badge>
                      <Settings20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Configurer le SDK</Heading>
                    </HStack>

                    <Text weight="medium">Variables d'environnement (.env) :</Text>
                    <CodeBlock
                      code={`VITE_WEBBA_API_KEY=your_webba_id_supabase_anon_key
VITE_WEBBA_CLIENT_ID=wba_xxx
VITE_WEBBA_CLIENT_SECRET=wbs_xxx
VITE_WEBBA_REDIRECT_URI=http://localhost:5173/auth/callback`}
                      language="bash"
                    />

                    <Text weight="medium">Configuration (main.tsx) :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

WebbaID.configure({
  apiKey: import.meta.env.VITE_WEBBA_API_KEY,
  clientId: import.meta.env.VITE_WEBBA_CLIENT_ID,
  clientSecret: import.meta.env.VITE_WEBBA_CLIENT_SECRET,
  redirectUri: import.meta.env.VITE_WEBBA_REDIRECT_URI
})`}
                      language="typescript"
                    />

                    <Banner variant="warning" title="Important">
                      Ne jamais exposer votre <InlineCode>clientSecret</InlineCode> dans du code public.
                      Utilisez des variables d'environnement.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            {/* Step 3 */}
            <div id="step3" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={200}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      <Badge variant="primary">Etape 3</Badge>
                      <Link20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Bouton de connexion</Heading>
                    </HStack>

                    <Text>
                      Ajoutez un bouton qui redirige vers Webba ID :
                    </Text>

                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

function LoginButton() {
  const handleLogin = async () => {
    await WebbaID.login()
    // L'utilisateur est redirige vers Webba ID
  }

  return (
    <button onClick={handleLogin}>
      Se connecter avec Webba ID
    </button>
  )
}`}
                      language="typescript"
                    />

                    <Card variant="subtle" padding="md">
                      <VStack gap="xs">
                        <Text size="sm" weight="medium">Le SDK gere automatiquement :</Text>
                        <Text size="sm">- Generation du code_verifier PKCE</Text>
                        <Text size="sm">- Generation du state CSRF</Text>
                        <Text size="sm">- Stockage securise dans cookie + localStorage</Text>
                      </VStack>
                    </Card>
                  </VStack>
                </Card>
              </Animate>
            </div>

            {/* Step 4 */}
            <div id="step4" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={250}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      <Badge variant="primary">Etape 4</Badge>
                      <Code20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Page callback</Heading>
                    </HStack>

                    <Text>
                      Creez la page <InlineCode>/auth/callback</InlineCode> pour gerer le retour :
                    </Text>

                    <CodeBlock
                      code={`// src/pages/AuthCallback.tsx
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WebbaID } from '@/lib/webba-id'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const processingRef = useRef(false)

  useEffect(() => {
    const processCallback = async () => {
      // Eviter double execution (React StrictMode)
      if (processingRef.current) return
      processingRef.current = true

      if (!WebbaID.isCallback()) {
        setError('Callback invalide')
        return
      }

      try {
        // Echange code -> tokens
        const tokens = await WebbaID.handleCallback()

        // Recuperer utilisateur
        const user = await WebbaID.getUser(tokens.accessToken)

        // Sauvegarder
        WebbaID.saveTokens(tokens)
        WebbaID.saveUser(user)

        navigate('/dashboard')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur')
        processingRef.current = false
      }
    }

    processCallback()
  }, [navigate])

  if (error) return <div>Erreur: {error}</div>
  return <div>Connexion en cours...</div>
}`}
                      language="typescript"
                    />

                    <HStack gap="md" style={{ flexWrap: 'wrap' }}>
                      <Badge>Code expire : 5 minutes</Badge>
                      <Badge>Usage unique</Badge>
                    </HStack>
                  </VStack>
                </Card>
              </Animate>
            </div>

            {/* Step 5 */}
            <div id="step5" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={300}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      <Badge variant="primary">Etape 5</Badge>
                      <Person20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Utiliser l'authentification</Heading>
                    </HStack>

                    <Text>
                      Recuperez l'utilisateur connecte dans vos composants :
                    </Text>

                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

// Verifier si connecte
const tokens = WebbaID.getStoredTokens()
const user = WebbaID.getStoredUser()

if (tokens && user && !WebbaID.isTokenExpired(tokens)) {
  console.log('Connecte:', user.email)
} else {
  console.log('Non connecte')
}

// Deconnexion
async function logout() {
  await WebbaID.logout(tokens?.accessToken)
  window.location.href = '/'
}`}
                      language="typescript"
                    />

                    <Badge>Token expire : 1 heure (refresh automatique disponible)</Badge>
                  </VStack>
                </Card>
              </Animate>
            </div>

            {/* Complete Example */}
            <div id="example" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={350}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <ShieldCheckmark20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Exemple complet avec AuthContext</Heading>
                    </HStack>

                    <Text weight="medium">1. main.tsx :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'
import { AuthProvider } from '@/contexts/AuthContext'

// Configurer le SDK
WebbaID.configure({
  apiKey: import.meta.env.VITE_WEBBA_API_KEY,
  clientId: import.meta.env.VITE_WEBBA_CLIENT_ID,
  clientSecret: import.meta.env.VITE_WEBBA_CLIENT_SECRET,
  redirectUri: import.meta.env.VITE_WEBBA_REDIRECT_URI
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <AuthProvider>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </AuthProvider>
)`}
                      language="typescript"
                    />

                    <Text weight="medium">2. AuthContext.tsx :</Text>
                    <CodeBlock
                      code={`import { createContext, useContext, useState, useEffect } from 'react'
import { WebbaID, AuthTokens, WebbaUser } from '@/lib/webba-id'

interface AuthContextType {
  user: WebbaUser | null
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  setAuth: (tokens: AuthTokens, user: WebbaUser) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState<WebbaUser | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const stored = WebbaID.getStoredTokens()
    const storedUser = WebbaID.getStoredUser()
    if (stored && storedUser && !WebbaID.isTokenExpired(stored)) {
      setTokens(stored)
      setUser(storedUser)
    }
    setIsLoading(false)
  }, [])

  const login = () => WebbaID.login()

  const logout = async () => {
    await WebbaID.logout(tokens?.accessToken)
    setUser(null)
    setTokens(null)
  }

  const setAuth = (t: AuthTokens, u: WebbaUser) => {
    WebbaID.saveTokens(t)
    WebbaID.saveUser(u)
    setTokens(t)
    setUser(u)
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, setAuth }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)!`}
                      language="typescript"
                    />

                    <Text weight="medium">3. Utilisation :</Text>
                    <CodeBlock
                      code={`import { useAuth } from '@/contexts/AuthContext'

function Header() {
  const { user, login, logout } = useAuth()

  return user ? (
    <>
      <span>Bonjour {user.name}</span>
      <button onClick={logout}>Deconnexion</button>
    </>
  ) : (
    <button onClick={login}>Connexion</button>
  )
}`}
                      language="typescript"
                    />
                  </VStack>
                </Card>
              </Animate>
            </div>

          </VStack>
        </div>

        {isDesktop && (
          <div style={{
            position: 'sticky',
            top: '2rem',
            width: '220px',
            flexShrink: 0
          }}>
            <TableOfContents
              items={tocItems}
              title="Sur cette page"
              autoTrack
              scrollOffset={100}
              variant="bordered"
            />
          </div>
        )}
      </HStack>
    </div>
  )
}
